import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Clock, Plus, Pencil, Trash2, Users, Settings, FileText, BookOpen, Volume2, PenTool } from 'lucide-react';
import axios from 'axios';
import { API_BASE as API } from '../../config/api';

const ACTION_ICONS = {
  created: Plus,
  updated: Pencil,
  deleted: Trash2,
};

const ENTITY_ICONS = {
  word: BookOpen,
  text: FileText,
  phoneme: Volume2,
  writing_system: PenTool,
  grapheme: PenTool,
  settings: Settings,
  share: Users,
  language: Settings,
};

const ACTION_COLORS = {
  created: 'text-green-400 bg-green-400/10',
  updated: 'text-blue-400 bg-blue-400/10',
  deleted: 'text-red-400 bg-red-400/10',
};

const ActivityLogPage = () => {
  const { conlangId } = useParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, [conlangId]);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/activity?limit=100`, { withCredentials: true });
      setEntries(res.data);
    } catch {}
    setLoading(false);
  };

  const formatTime = (isoStr) => {
    const date = new Date(isoStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  // Group entries by date
  const groupByDate = (entries) => {
    const groups = {};
    entries.forEach(entry => {
      const date = new Date(entry.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
      if (!groups[date]) groups[date] = [];
      groups[date].push(entry);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const grouped = groupByDate(entries);

  return (
    <div className="max-w-3xl" data-testid="activity-log-page">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="activity-log-title">Activity Log</h2>
        <p className="text-foreground-muted text-sm mt-1">Recent changes made to this language</p>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-16" data-testid="activity-log-empty">
          <Clock className="w-12 h-12 text-foreground-subtle mx-auto mb-3" />
          <h3 className="font-heading text-lg text-foreground mb-1">No activity yet</h3>
          <p className="text-foreground-muted text-sm">Changes to this language will appear here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">{date}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-0">
                {items.map((entry, idx) => {
                  const ActionIcon = ACTION_ICONS[entry.action] || Pencil;
                  const EntityIcon = ENTITY_ICONS[entry.entity_type] || Settings;
                  const colorClass = ACTION_COLORS[entry.action] || 'text-foreground-muted bg-accent';

                  return (
                    <div
                      key={entry.log_id}
                      className="flex items-start gap-3 py-2.5 group"
                      data-testid={`activity-entry-${entry.log_id}`}
                    >
                      {/* Timeline connector */}
                      <div className="flex flex-col items-center">
                        <div className={`p-1.5 rounded-full ${colorClass}`}>
                          <ActionIcon className="w-3.5 h-3.5" />
                        </div>
                        {idx < items.length - 1 && (
                          <div className="w-px h-full min-h-[16px] bg-border mt-1" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pb-1">
                        <p className="text-sm text-foreground leading-snug">
                          <span className="font-medium">{entry.user_name}</span>
                          {' '}
                          <span className="text-foreground-muted">
                            {entry.action === 'created' && 'added'}
                            {entry.action === 'updated' && 'updated'}
                            {entry.action === 'deleted' && 'removed'}
                          </span>
                          {' '}
                          <span className="text-foreground-muted">{entry.entity_type}</span>
                          {entry.entity_name && (
                            <span className="font-medium text-foreground"> "{entry.entity_name}"</span>
                          )}
                        </p>
                        {entry.detail && (
                          <p className="text-xs text-foreground-subtle mt-0.5">{entry.detail}</p>
                        )}
                        <span className="text-xs text-foreground-subtle">{formatTime(entry.created_at)}</span>
                      </div>

                      {/* Entity icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        <EntityIcon className="w-4 h-4 text-foreground-subtle" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;
